import { useCallback, useContext } from 'react';
import { Position, Station } from '../../types';
import { ZoneContext } from '../../../../context/ZoneContext';
import { Path } from '../../MyMap/Map/components/ZoneMap/components/Path';
import { MapTourContext } from 'context/MapTourContext';
import { MAP_SPECIFIC_TOUR_START_INDEX, TourStep, TourType, tourSteps } from 'pages/Map/components/MapTour';
import { useStorage } from '@medly-components/utils';
import { UserContext } from 'context/UserContext';
import { MapSearchContext } from 'context/MapSearchContext';

export interface ITour {
    station?: Station | undefined;
    path?: Path | undefined;
    position?: Position | undefined;
    type: TourType;
    currentStep: number;
}

export const MapTourFinishedKey = 'is-map-tour-finished';
export const MapTourStepIndex = 'map-tour-step-index';

export const useMapTour = () => {
    const { stations, paths } = useContext(ZoneContext);
    const { getKeyWithUserId } = useContext(UserContext);
    const { tour, setTour, setIsMapTour } = useContext(MapTourContext);
    const { closeSearch } = useContext(MapSearchContext);
    const [tourStepIndex, setTourStepIndex] = useStorage<number>(getKeyWithUserId(MapTourStepIndex), {
        initialValue: MAP_SPECIFIC_TOUR_START_INDEX
    });

    const getStation = (type: TourType) => {
        const firstStation = stations[0];
        const firstStationXValue = firstStation.getAbsoluteXValue();
        if ([TourType.STATION_AND_DRAWERS, TourType.INFO_ABOUT_STATIONS, TourType.PLAN_UR_LIFE, TourType.INFO_ABOUT_PATHS].includes(type)) {
            return stations.find(station => station.getAbsoluteXValue() === firstStationXValue && !station.getParentStation());
        }
        return;
    };

    const updateTour = useCallback(
        (index: number) => {
            //We are storing map tour index in local storage and it shouldn't effect even if we remove any step in future
            const currentStep = Math.max(Math.min(index, tourSteps.length - 1), MAP_SPECIFIC_TOUR_START_INDEX);
            const { type } = tourSteps[currentStep] as TourStep;
            if (type === TourType.ZOOM_IN_ZOOM_OUT) {
                setTourStepIndex(currentStep);
                setTour(tour => ({ ...tour, station: undefined, path: undefined, currentStep, type }));
                return;
            }
            if (!stations.length) {
                return;
            }

            const tourStation = getStation(type);
            //Finding path which aligns with first station
            let tourPath: Path | undefined = undefined;
            const firstStation = stations[0];
            const firstStationXValue = firstStation.getAbsoluteXValue();
            if (type === TourType.INFO_ABOUT_PATHS) {
                tourPath = paths.find(path => path.source.getAbsoluteXValue() === firstStationXValue);
            }
            setTourStepIndex(currentStep);
            setTour(tour => ({
                ...tour,
                station: tourStation,
                path: tourPath,
                type,
                currentStep,
                position: tourStation?.getAbsolutePosition() ?? tourPath?.source.getAbsolutePosition()
            }));
        },
        [stations, paths, setTour]
    );

    const startTour = useCallback(() => {
        setIsMapTour(true);
        closeSearch();
        if (tour) return;

        updateTour(tourStepIndex);
    }, [tour, updateTour, tourStepIndex]);

    const finishTour = useCallback(() => {
        localStorage.setItem(getKeyWithUserId(MapTourFinishedKey), 'true');
        setTourStepIndex(MAP_SPECIFIC_TOUR_START_INDEX);
    }, []);

    const isTourFinished = useCallback(() => {
        return localStorage.getItem(getKeyWithUserId(MapTourFinishedKey)) === 'true';
    }, []);

    return { updateTour, finishTour, isTourFinished, startTour, setTourStepIndex };
};
